import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import cls from "./LoungeMemberFriends.module.scss";

import api from "@/utils/axios";
import avatarPng from "@/assets/UI/avatar.png";
import TextPostCard from "@/components/Post/TextPostCard/TextPostCard";
import PhotoPostCard from "@/components/Post/PhotoPostCard/PhotoPostCard";
import VideoPostCard from "@/components/Post/VideoPostCard/VideoPostCard";

const LoungeMemberFriends = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { memberId } = useParams();

  const [friends, setFriends] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState("");
  const [friendsOnlyPosts, setFriendsOnlyPosts] = useState([]);
  const [friendsOnlyPostsTotal, setFriendsOnlyPostsTotal] = useState(0);
  const [friendsOnlyPostsLoading, setFriendsOnlyPostsLoading] = useState(false);
  const [friendsOnlyPostsError, setFriendsOnlyPostsError] = useState("");

  const loadFriends = async (nextPage = 1, replace = false) => {
    if (!memberId) return;

    try {
      setLoading(true);
      setError("");

      const { data } = await api.get(`/v1/friends/member/${memberId}`, {
        params: { page: nextPage },
        skipToast: true,
      });

      const list = data?.friends || [];
      setFriends((prev) => (replace ? list : [...prev, ...list]));
      setPage(data?.page || nextPage);
      setHasMore(Boolean(data?.hasMore));
      setInitialLoaded(true);
    } catch (err) {
      console.error("Failed to load member friends", err);
      setError(
        err?.response?.data?.message || t("loungeMemberFriends.state.error"),
      );
      setInitialLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFriends(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  useEffect(() => {
    if (!memberId) return;

    let ignore = false;

    (async () => {
      try {
        setFriendsOnlyPostsLoading(true);
        setFriendsOnlyPostsError("");

        const { data } = await api.get(
          `/v1/posts/member-friends-feed/${memberId}`,
          {
            params: { page: 1, limit: 15 },
            skipToast: true,
          },
        );

        if (ignore) return;

        const rows =
          data?.rows || data?.posts || data?.data || data?.items || [];
        setFriendsOnlyPosts(Array.isArray(rows) ? rows : []);
        setFriendsOnlyPostsTotal(data?.meta?.total ?? rows.length);
      } catch (err) {
        console.error("Failed to load member friends-only posts", err);
        if (!ignore) {
          setFriendsOnlyPostsError(
            err?.response?.data?.message ||
              t("memberProfileOverview.posts.error"),
          );
        }
      } finally {
        if (!ignore) {
          setFriendsOnlyPostsLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [memberId, t]);

  const friendsKnowPersonally = useMemo(
    () => friends.filter((f) => f.connectionType === "know_personally"),
    [friends],
  );

  const friendsOnlineOnly = useMemo(
    () => friends.filter((f) => f.connectionType === "online_only"),
    [friends],
  );

  const friendsOther = useMemo(
    () =>
      friends.filter(
        (f) =>
          f.connectionType !== "know_personally" &&
          f.connectionType !== "online_only",
      ),
    [friends],
  );

  const totalCount = friends.length;

  const renderFeedPost = (post) => {
    const type = String(post?.type || "").toLowerCase();
    const hasVideo = Boolean(post?.videoId || post?.video?.id || post?.video);
    const hasImages =
      (Array.isArray(post?.images) && post.images.length > 0) ||
      (Array.isArray(post?.imageUrls) && post.imageUrls.length > 0);

    if (type === "video-post" || type === "video") {
      return <VideoPostCard post={post} />;
    }
    if (type === "photo-post" || type === "photo") {
      return <PhotoPostCard post={post} />;
    }
    if (type === "text-only" || type === "text") {
      return <TextPostCard post={post} />;
    }

    if (hasVideo) return <VideoPostCard post={post} />;
    if (hasImages) return <PhotoPostCard post={post} />;
    return <TextPostCard post={post} />;
  };

  const handleViewProfile = (friend) => {
    const targetMemberId = friend?.member?._id;
    if (!targetMemberId) return;
    navigate(`/lounge/member-profile/${targetMemberId}`);
  };

  const renderFriendCard = (friend) => {
    const user = friend.user || {};
    const member = friend.member || {};
    const avatarSrc = member.avatar?.url || avatarPng;

    const memberTypeKey = member.memberType || "unknown";
    const genderKey = member.gender || "unknown";

    const memberTypeLabel = t(
      `myFriends.friend.memberType.${memberTypeKey}`,
      memberTypeKey,
    );
    const genderLabel = t(`myFriends.friend.gender.${genderKey}`, genderKey);

    let friendsSinceLabel = t("myFriends.friend.friendsSinceUnknown");
    if (friend.friendsSince) {
      const d = new Date(friend.friendsSince);
      if (!Number.isNaN(d.getTime())) {
        friendsSinceLabel = t("myFriends.friend.friendsSince", {
          date: d.toLocaleDateString(),
        });
      }
    }

    return (
      <article key={friend.id} className={cls.friendCard}>
        <img
          src={avatarSrc}
          alt={user.username || t("myFriends.friend.unknownUsername")}
          className={cls.friendAvatar}
        />

        <div className={cls.friendMain}>
          <div className={cls.friendNameRow}>
            <span className={cls.friendUsername}>
              {user.username || t("myFriends.friend.unknownUsername")}
            </span>
          </div>

          <div className={cls.friendBadges}>
            {memberTypeKey && (
              <span className={cls.friendBadge}>{memberTypeLabel}</span>
            )}
            {genderKey && (
              <span className={cls.friendBadge}>{genderLabel}</span>
            )}
          </div>

          <div className={cls.friendMetaRow}>
            <span className={cls.friendMetaItem}>{friendsSinceLabel}</span>
          </div>

          <div className={cls.friendActions}>
            <button
              type="button"
              className={cls.friendViewBtn}
              onClick={() => handleViewProfile(friend)}
            >
              {t("myFriends.friend.viewProfile")}
            </button>
          </div>
        </div>
      </article>
    );
  };

  const renderGroupSection = (titleKey, emptyKey, list) => (
    <section className={cls.card}>
      <div className={cls.cardHeaderRow}>
        <div>
          <h2 className={cls.cardTitle}>{t(titleKey)}</h2>
          <span className={cls.cardMeta}>
            {t("loungeMemberFriends.countLabel", { count: list.length })}
          </span>
        </div>
      </div>

      {!loading && initialLoaded && list.length === 0 && (
        <p className={cls.friendEmptyText}>{t(emptyKey)}</p>
      )}

      {list.length > 0 && (
        <div className={cls.friendsList}>
          {list.map((f) => renderFriendCard(f))}
        </div>
      )}
    </section>
  );

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        {/* Tabs row */}
        <nav className={cls.tabs}>
          <Link
            to={`/lounge/member-profile/${memberId}`}
            className={cls.tabButton}
          >
            {t("memberProfileOverview.tabs.timeline")}
          </Link>

          {/* About tab for OTHER member */}
          <Link
            to={`/lounge/member-profile/${memberId}/about`}
            className={cls.tabButton}
          >
            {t("memberProfileOverview.tabs.about")}
          </Link>
          <Link
            to={`/lounge/member-profile/${memberId}/friends`}
            className={`${cls.tabButton} ${cls.activeTab}`}
          >
            {t("memberProfileOverview.tabs.friends")}
          </Link>
        </nav>

        {/* Main layout */}
        <div className={cls.layout}>
          <div className={cls.leftColumn}>
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <div>
                  <h2 className={cls.cardTitle}>
                    {t("loungeMemberFriends.title")}
                  </h2>
                  <span className={cls.cardMeta}>
                    {t("loungeMemberFriends.countLabel", {
                      count: totalCount,
                    })}
                  </span>
                </div>
              </div>

              {!initialLoaded && loading && (
                <p className={cls.friendsStatusText}>
                  {t("loungeMemberFriends.state.loading")}
                </p>
              )}

              {initialLoaded && !loading && error && (
                <p className={cls.friendsStatusText}>{error}</p>
              )}

              {initialLoaded && !loading && !error && totalCount === 0 && (
                <p className={cls.friendEmptyText}>
                  {t("loungeMemberFriends.state.noFriends")}
                </p>
              )}
            </section>

            {friendsKnowPersonally.length > 0 || initialLoaded
              ? renderGroupSection(
                  "loungeMemberFriends.groups.knowPersonallyTitle",
                  "loungeMemberFriends.groups.knowPersonallyEmpty",
                  friendsKnowPersonally,
                )
              : null}

            {friendsOnlineOnly.length > 0 || initialLoaded
              ? renderGroupSection(
                  "loungeMemberFriends.groups.onlineOnlyTitle",
                  "loungeMemberFriends.groups.onlineOnlyEmpty",
                  friendsOnlineOnly,
                )
              : null}

            {friendsOther.length > 0 &&
              renderGroupSection(
                "loungeMemberFriends.groups.otherTitle",
                "loungeMemberFriends.groups.otherEmpty",
                friendsOther,
              )}

            {hasMore && (
              <div className={cls.loadMoreRow}>
                <button
                  type="button"
                  className={cls.loadMoreBtn}
                  onClick={() => loadFriends(page + 1)}
                  disabled={loading}
                >
                  {loading
                    ? t("loungeMemberFriends.state.loading")
                    : t("loungeMemberFriends.state.loadMore")}
                </button>
              </div>
            )}
          </div>

          <div className={cls.rightColumn}>
            <section className={cls.card}>
              <div className={cls.cardHeaderRow}>
                <div>
                  <h2 className={cls.cardTitle}>
                    {t("memberProfileOverview.postsTitle")}
                  </h2>
                  {!friendsOnlyPostsLoading &&
                    !friendsOnlyPostsError &&
                    friendsOnlyPosts.length > 0 && (
                      <span className={cls.cardMeta}>
                        {t("memberProfileOverview.postsCountFriends", {
                          count: friendsOnlyPostsTotal,
                        })}
                      </span>
                    )}
                </div>
              </div>

              {friendsOnlyPostsLoading && (
                <p className={cls.friendsStatusText}>
                  {t("memberProfileOverview.posts.loading")}
                </p>
              )}

              {!friendsOnlyPostsLoading && friendsOnlyPostsError && (
                <p className={cls.friendsStatusText}>
                  {friendsOnlyPostsError}{" "}
                </p>
              )}

              {!friendsOnlyPostsLoading &&
                !friendsOnlyPostsError &&
                friendsOnlyPostsTotal === 0 && (
                  <p className={cls.friendEmptyText}>
                    {t("memberProfileOverview.posts.empty")}
                  </p>
                )}

              {!friendsOnlyPostsLoading &&
                !friendsOnlyPostsError &&
                friendsOnlyPosts.length > 0 && (
                  <div className={cls.postList}>
                    {friendsOnlyPosts.map((post) => (
                      <div key={post._id} className={cls.postCardWrap}>
                        {renderFeedPost(post)}
                      </div>
                    ))}
                  </div>
                )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoungeMemberFriends;
